import { registry, type IUIRuntime } from '@cian/mf-registry/browser';
import { isUIRuntime } from '@cian/mf-registry/shared';
import { ConfigurationError } from '@cian/peperrors/shared';

import type { IHeaderUIAPIController, IHeaderUIAPIScope } from './types';

const HEADER_UI_API_BRIDGE_KEY = Symbol.for('header-frontend.ui-api-bridge.v4');

interface IHeaderUIAPIControllerRegistration {
  controller: IHeaderUIAPIController;
  token: symbol;
}

interface IHeaderUIAPIBridge {
  availableControllers: Map<string, IHeaderUIAPIControllerRegistration>;
  activeControllers: IHeaderUIAPIControllerRegistration[];
  pendingEnsures: Map<string, Promise<void>>;
}

function getScopeKey(scope: IHeaderUIAPIScope): string {
  return JSON.stringify([scope.microfrontendName, scope.imageVersion]);
}

function getBridge(): IHeaderUIAPIBridge {
  const existingBridge = Reflect.get(globalThis, HEADER_UI_API_BRIDGE_KEY) as IHeaderUIAPIBridge | undefined;

  if (existingBridge) {
    return existingBridge;
  }

  const bridge: IHeaderUIAPIBridge = {
    availableControllers: new Map(),
    activeControllers: [],
    pendingEnsures: new Map(),
  };
  Reflect.set(globalThis, HEADER_UI_API_BRIDGE_KEY, bridge);

  return bridge;
}

export function registerHeaderUIAPIController(
  scope: IHeaderUIAPIScope,
  controller: IHeaderUIAPIController,
): () => void {
  const bridge = getBridge();
  const scopeKey = getScopeKey(scope);
  const token = Symbol();

  if (!bridge.availableControllers.has(scopeKey)) {
    bridge.availableControllers.set(scopeKey, { controller, token });
  }

  let isDisposed = false;

  return () => {
    if (isDisposed) {
      return;
    }

    isDisposed = true;

    if (bridge.availableControllers.get(scopeKey)?.token === token) {
      bridge.availableControllers.delete(scopeKey);
    }
  };
}

export function activateHeaderUIAPIController(controller: IHeaderUIAPIController): () => void {
  const bridge = getBridge();
  const token = Symbol();

  bridge.activeControllers.push({ controller, token });

  let isDisposed = false;

  return () => {
    if (isDisposed) {
      return;
    }

    isDisposed = true;

    const registrationIndex = bridge.activeControllers.findIndex(registration => registration.token === token);

    if (registrationIndex !== -1) {
      bridge.activeControllers.splice(registrationIndex, 1);
    }
  };
}

function getHeaderUIAPIController(scopeKey: string): IHeaderUIAPIController | undefined {
  const { activeControllers, availableControllers } = getBridge();

  return activeControllers[activeControllers.length - 1]?.controller ?? availableControllers.get(scopeKey)?.controller;
}

function createMissingUIRuntimeError(scope: IHeaderUIAPIScope): ConfigurationError {
  return new ConfigurationError({
    message: `Header UI Runtime is not loaded for ${scope.microfrontendName}#${scope.imageVersion}. Загрузите UI Runtime заголовка перед вызовом Header API.`,
    domain: 'header-frontend.api-runtime.active-ui',
  });
}

async function ensureHeaderUIRuntime(scope: IHeaderUIAPIScope): Promise<void> {
  const microfrontend = registry().microfrontend(scope.microfrontendName);
  const image = microfrontend?.image(scope.imageVersion);
  const uiRuntime =
    image &&
    Object.values(image.runtimes)
      .map(runtimeManifest => image.runtimeStrict(runtimeManifest.name))
      .find(runtime => isUIRuntime<IUIRuntime>(runtime));

  if (!uiRuntime) {
    throw createMissingUIRuntimeError(scope);
  }

  await uiRuntime.ensure();
}

export function getHeaderUIAPIControllerStrict(scope: IHeaderUIAPIScope): IHeaderUIAPIController {
  const controller = getHeaderUIAPIController(getScopeKey(scope));

  if (!controller) {
    throw createMissingUIRuntimeError(scope);
  }

  return controller;
}

export async function getOrEnsureHeaderUIAPIController(scope: IHeaderUIAPIScope): Promise<IHeaderUIAPIController> {
  const bridge = getBridge();
  const scopeKey = getScopeKey(scope);
  const existingController = getHeaderUIAPIController(scopeKey);

  if (existingController) {
    return existingController;
  }

  let pendingEnsure = bridge.pendingEnsures.get(scopeKey);

  if (!pendingEnsure) {
    pendingEnsure = ensureHeaderUIRuntime(scope);
    bridge.pendingEnsures.set(scopeKey, pendingEnsure);
  }

  try {
    await pendingEnsure;
  } finally {
    if (bridge.pendingEnsures.get(scopeKey) === pendingEnsure) {
      bridge.pendingEnsures.delete(scopeKey);
    }
  }

  return getHeaderUIAPIControllerStrict(scope);
}
